// Members' "Live now": a group class running right now (from /api/live-now),
// shown as a card under Live classes in AF Studio and as the nav's pill.

import { hasPremium } from './premium';

// What the trainer page saves (or shows) when the title is left blank.
const UNTITLED = /^untitled (stream|class)$/i;

/** The title the trainer typed, or '' when it was left blank. */
export const liveClassOwnTitle = (liveClass) => {
  const title = (liveClass?.title || '').trim();
  return title && !UNTITLED.test(title) ? title : '';
};

/** The class's title, or "Live class with Alvaro" when it has none. */
export const liveClassTitle = (liveClass) => {
  const title = liveClassOwnTitle(liveClass);
  if (title) return title;
  return liveClass?.trainerName
    ? `Live class with ${liveClass.trainerName}`
    : 'Live class';
};

/** "Just started" / "Started 12 min ago" / "Started 1 hr 5 min ago". */
export const startedAgo = (startedAt, now) => {
  if (!Number.isFinite(startedAt) || !Number.isFinite(now)) return '';
  const minutes = Math.floor(Math.max(0, now - startedAt) / 60000);
  if (minutes < 1) return 'Just started';
  if (minutes < 60) return `Started ${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return `Started ${hours} hr${rest ? ` ${rest} min` : ''} ago`;
};

// Same rule the server applies to private classes (validateViewerAccess in
// server.js): admins, Premium members and users with free-trial classes left.
export const canJoinPrivateClass = (user) =>
  !!user &&
  (user.role?.includes('admin') ||
    hasPremium(user) ||
    (!user.freeTrialUsed && (user.freeTrialSessionsRemaining || 0) > 0));

/**
 * What Join does for this member: 'join', 'sign-in' (a Premium class, signed
 * out) or 'upgrade' (a Premium class they can't join yet). A free class is
 * open to anyone, as its link is.
 */
export const liveClassJoinStep = (liveClass, { isAuthenticated, user }) => {
  if (liveClass?.isPublic) return 'join';
  if (!isAuthenticated) return 'sign-in';
  return canJoinPrivateClass(user) ? 'join' : 'upgrade';
};

/** Live classes not already shown as a scheduled class's card. */
export const unscheduledLiveClasses = (liveClasses, scheduledStreams) => {
  const scheduledIds = new Set(
    (scheduledStreams || []).map((stream) => String(stream._id))
  );
  return (liveClasses || []).filter(
    (liveClass) => !scheduledIds.has(String(liveClass.streamId))
  );
};

/**
 * Where to send someone to the class that's live right now: one class →
 * its page, more than one → the Live classes list. Null when none is on.
 * `exceptId` leaves out the class whose page they're already on.
 */
export const liveClassLink = (liveClasses, { isAuthenticated = false, exceptId } = {}) => {
  const others = (liveClasses || []).filter(
    (liveClass) =>
      liveClass && liveClass.streamId && String(liveClass.streamId) !== String(exceptId)
  );
  if (others.length === 0) return null;
  if (others.length === 1) {
    return { href: `/watch-stream/${others[0].streamId}`, liveClass: others[0] };
  }
  return {
    href: isAuthenticated ? '/dashboard/videos/all' : '/videos/all',
    liveClass: null,
  };
};
